import { formatMoney, pricingTotals } from "@/lib/format";
import { safeHtml } from "@/lib/safeHtml";

const COVER_STYLES = {
  editorial: "border-l-4 pl-6",
  bold: "text-white",
  minimal: "text-center",
  classic: "border-2 p-8 text-center",
};

function Cover({ block, brand, docTitle, clientName, orgName }) {
  const d = block.data || {};
  const style = d.style || brand.cover_style || "editorial";
  const accent = brand.accent_color;
  const isBold = style === "bold";
  return (
    <div
      className={`mb-10 py-10 ${COVER_STYLES[style] || ""}`}
      style={{
        borderColor: accent,
        background: isBold ? accent : undefined,
        color: isBold ? "#fff" : undefined,
        padding: isBold ? "40px 32px" : undefined,
        borderRadius: isBold ? 8 : undefined,
      }}
    >
      {brand.logo_url ? (
        <img src={brand.logo_url} alt="logo" className="h-8 mb-6 object-contain" />
      ) : (
        <p className="text-[11px] uppercase tracking-[0.28em] mb-6" style={{ color: isBold ? "#fff" : accent }}>
          {orgName || "Proposal"}
        </p>
      )}
      <h1 className="text-3xl sm:text-4xl font-extrabold leading-tight">{d.title || docTitle}</h1>
      {d.subtitle && <p className="mt-3 text-base opacity-80">{d.subtitle}</p>}
      <div className="mt-6 text-sm opacity-70 space-y-0.5">
        {clientName && <p>Prepared for {clientName}</p>}
        {d.prepared_by && <p>Prepared by {d.prepared_by}</p>}
        <p>{new Date().toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })}</p>
      </div>
    </div>
  );
}

function PricingBlock({ block, currency, gstMode }) {
  const d = block.data || {};
  const t = pricingTotals(d.lines || [], d.gst_mode || gstMode);
  return (
    <div className="mb-8">
      {d.title && <h3 className="text-lg font-semibold mb-3">{d.title}</h3>}
      <div className="overflow-hidden rounded-lg border" style={{ borderColor: "rgba(0,0,0,0.1)" }}>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left" style={{ background: "rgba(0,0,0,0.04)" }}>
              <th className="px-3 py-2 font-semibold">Item</th>
              <th className="px-3 py-2 font-semibold text-right">Qty</th>
              <th className="px-3 py-2 font-semibold text-right">Rate</th>
              <th className="px-3 py-2 font-semibold text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {(d.lines || []).map((l, i) => (
              <tr key={l.id || i} style={{ borderTop: "1px solid rgba(0,0,0,0.07)" }}>
                <td className="px-3 py-2">
                  <div className="font-medium">{l.name}</div>
                  {l.description && <div className="opacity-60 text-xs">{l.description}</div>}
                </td>
                <td className="px-3 py-2 text-right font-num">{l.qty}</td>
                <td className="px-3 py-2 text-right font-num">{formatMoney(l.unit_price_minor, currency)}</td>
                <td className="px-3 py-2 text-right font-num">{formatMoney(Math.round(l.qty * l.unit_price_minor), currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-3 ml-auto max-w-xs text-sm space-y-1">
        <Row label="Subtotal" value={formatMoney(t.subtotal, currency)} />
        {t.discount > 0 && <Row label="Discount" value={`-${formatMoney(t.discount, currency)}`} />}
        {Object.entries(t.breakdown).map(([k, v]) => (
          <Row key={k} label={k} value={formatMoney(v, currency)} />
        ))}
        {!Object.keys(t.breakdown).length && t.tax > 0 && <Row label="Tax" value={formatMoney(t.tax, currency)} />}
        <div className="flex justify-between pt-2 mt-1 border-t font-semibold" style={{ borderColor: "rgba(0,0,0,0.12)" }}>
          <span>Total</span>
          <span className="font-num">{formatMoney(t.total, currency)}</span>
        </div>
      </div>
      {d.note && <p className="mt-2 text-xs opacity-60">{d.note}</p>}
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between">
    <span className="opacity-70">{label}</span>
    <span className="font-num">{value}</span>
  </div>
);

export function BlockView({ block, brand, currency, gstMode, docTitle, clientName, orgName, onTierSelect, selectedTier, decision }) {
  const d = block.data || {};
  const accent = brand.accent_color;
  switch (block.type) {
    case "cover":
      return <Cover block={block} brand={brand} docTitle={docTitle} clientName={clientName} orgName={orgName} />;
    case "heading": {
      const Tag = `h${Math.min(4, Math.max(1, d.level || 2))}`;
      const size = { 1: "text-3xl", 2: "text-2xl", 3: "text-xl", 4: "text-lg" }[d.level || 2];
      return <Tag className={`${size} font-bold mt-8 mb-3`}>{d.text}</Tag>;
    }
    case "text":
      return (
        <div
          className="mb-5 text-sm leading-7 [&_p]:mb-3 [&_strong]:font-semibold"
          dangerouslySetInnerHTML={{ __html: safeHtml(d.html) }}
        />
      );
    case "bullets":
      return (
        <ul className="mb-6 space-y-2">
          {(d.items || []).map((it, i) => (
            <li key={i} className="flex gap-2.5 text-sm leading-6">
              <span className="mt-2 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: accent }} />
              <span>{it}</span>
            </li>
          ))}
        </ul>
      );
    case "keyvalue":
      return (
        <div className="mb-6 divide-y" style={{ borderColor: "rgba(0,0,0,0.08)" }}>
          {(d.rows || []).map((r, i) => (
            <div key={i} className="flex justify-between gap-4 py-2 text-sm" style={{ borderTop: i ? "1px solid rgba(0,0,0,0.08)" : "none" }}>
              <span className="opacity-60">{r.key}</span>
              <span className="font-medium text-right">{r.value}</span>
            </div>
          ))}
        </div>
      );
    case "pricing":
      return <PricingBlock block={block} currency={currency} gstMode={gstMode} />;
    case "tiered_pricing":
      return (
        <div className="mb-8 grid gap-3 sm:grid-cols-3">
          {(d.tiers || []).map((t) => {
            const active = selectedTier ? selectedTier === t.id : t.highlight;
            return (
              <button
                key={t.id}
                type="button"
                data-testid={`tier-option-${t.id}`}
                onClick={() => onTierSelect?.(t.id)}
                className="text-left rounded-xl p-4 border transition-all"
                style={{
                  borderColor: active ? accent : "rgba(0,0,0,0.12)",
                  boxShadow: active ? `0 0 0 2px ${accent}33` : "none",
                  cursor: onTierSelect ? "pointer" : "default",
                }}
              >
                <p className="text-xs uppercase tracking-widest opacity-60">{t.name}</p>
                <p className="text-xl font-bold font-num mt-1">{formatMoney(t.price_minor, currency)}</p>
                <p className="text-xs opacity-60">{t.cadence}</p>
                <ul className="mt-3 space-y-1.5">
                  {(t.features || []).map((f, i) => (
                    <li key={i} className="text-xs flex gap-2">
                      <span style={{ color: accent }}>✓</span>
                      {f}
                    </li>
                  ))}
                </ul>
              </button>
            );
          })}
        </div>
      );
    case "timeline":
      return (
        <div className="mb-8 relative pl-6">
          <div className="absolute left-[5px] top-1 bottom-1 w-px" style={{ background: "rgba(0,0,0,0.15)" }} />
          {(d.steps || []).map((s, i) => (
            <div key={i} className="relative mb-5">
              <span className="absolute -left-6 top-1 w-3 h-3 rounded-full" style={{ background: accent }} />
              <p className="text-sm font-semibold">{s.label}</p>
              <p className="text-xs uppercase tracking-wide opacity-50 font-num">{s.duration}</p>
              {s.detail && <p className="text-sm opacity-75 mt-1">{s.detail}</p>}
            </div>
          ))}
        </div>
      );
    case "case_study":
      return (
        <div className="mb-8 rounded-xl p-5" style={{ background: "rgba(0,0,0,0.04)" }}>
          <p className="text-xs uppercase tracking-widest opacity-60">Case study</p>
          <p className="text-lg font-bold mt-1">{d.client}</p>
          <p className="text-xl font-num font-bold mt-2" style={{ color: accent }}>{d.result}</p>
          <p className="text-sm mt-2 leading-6 opacity-80">{d.body}</p>
          {d.image_url && <img src={d.image_url} alt="" className="mt-3 rounded-lg w-full object-cover max-h-48" />}
        </div>
      );
    case "team_member":
      return (
        <div className="mb-6 flex gap-4 items-start">
          {d.image_url ? (
            <img src={d.image_url} alt={d.name} className="w-14 h-14 rounded-full object-cover" />
          ) : (
            <div className="w-14 h-14 rounded-full grid place-items-center text-sm font-semibold" style={{ background: `${accent}22`, color: accent }}>
              {(d.name || "?").slice(0, 2).toUpperCase()}
            </div>
          )}
          <div>
            <p className="font-semibold">{d.name}</p>
            <p className="text-xs uppercase tracking-wide opacity-60">{d.role}</p>
            {d.bio && <p className="text-sm mt-1 opacity-80 leading-6">{d.bio}</p>}
          </div>
        </div>
      );
    case "terms":
      return (
        <div className="mb-8 space-y-3">
          {(d.clauses || []).map((c, i) => (
            <div key={i}>
              <p className="text-sm font-semibold">
                {i + 1}. {c.title}
              </p>
              <p className="text-sm opacity-75 leading-6">{c.body}</p>
            </div>
          ))}
        </div>
      );
    case "signature": {
      const signed = decision?.outcome === "accepted" ? decision : null;
      if (signed) {
        const when = signed.accepted_at ? new Date(signed.accepted_at) : null;
        return (
          <div
            className="mb-6 mt-8 rounded-xl border-2 p-5"
            style={{ borderColor: "#05966955", background: "rgba(5,150,105,0.05)" }}
            data-testid="signature-block-signed"
          >
            <div className="flex items-center gap-2 mb-4">
              <span
                className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold"
                style={{ background: "rgba(5,150,105,0.12)", color: "#047857" }}
              >
                ✓ Accepted &amp; signed
              </span>
            </div>
            <p className="text-sm opacity-80">{d.prompt}</p>
            <div className="mt-5 flex flex-wrap gap-8">
              <div className="flex-1 min-w-[180px]">
                <p className="text-lg font-semibold" data-testid="signature-signer-name">
                  {signed.signer_name}
                </p>
                <p className="text-xs opacity-60 mt-0.5">
                  {signed.signer_role || d.signer_role || "Client representative"}
                </p>
                {signed.signer_email && <p className="text-xs opacity-50 mt-0.5">{signed.signer_email}</p>}
              </div>
              <div>
                <p className="text-lg font-semibold" data-testid="signature-signed-date">
                  {when
                    ? when.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
                    : "—"}
                </p>
                <p className="text-xs opacity-60 mt-0.5">
                  Accepted on{when ? ` ${when.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}` : ""}
                </p>
              </div>
            </div>
            {signed.fingerprint && (
              <p className="text-[11px] opacity-45 mt-4 font-mono break-all">
                Verification fingerprint {String(signed.fingerprint).slice(0, 32)}
              </p>
            )}
          </div>
        );
      }
      return (
        <div className="mb-6 mt-8 rounded-xl border-2 border-dashed p-5" style={{ borderColor: `${accent}55` }} data-testid="signature-block-unsigned">
          <p className="text-sm opacity-80">{d.prompt}</p>
          <div className="mt-6 flex gap-8">
            <div className="flex-1">
              <div className="border-b" style={{ borderColor: "rgba(0,0,0,0.25)" }} />
              <p className="text-xs opacity-50 mt-1">{d.signer_role || "Signature"}</p>
            </div>
            <div className="w-32">
              <div className="border-b" style={{ borderColor: "rgba(0,0,0,0.25)" }} />
              <p className="text-xs opacity-50 mt-1">Date</p>
            </div>
          </div>
        </div>
      );
    }
    case "image":
      return d.url ? (
        <figure className="mb-6">
          <img src={d.url} alt={d.caption || ""} className={`rounded-lg object-cover ${d.width === "half" ? "w-1/2" : "w-full"}`} />
          {d.caption && <figcaption className="text-xs opacity-55 mt-1.5">{d.caption}</figcaption>}
        </figure>
      ) : (
        <div className="mb-6 rounded-lg border border-dashed p-8 text-center text-xs opacity-50">Image placeholder</div>
      );
    case "page_break":
      return (
        <div className="my-8 flex items-center gap-3 text-[10px] uppercase tracking-widest opacity-40">
          <div className="flex-1 border-t border-dashed" /> page break <div className="flex-1 border-t border-dashed" />
        </div>
      );
    case "spacer":
      return <div style={{ height: d.height || 32 }} />;
    default:
      return null;
  }
}

export const DocPreview = ({ proposal, brand, orgName, onTierSelect, selectedTier, testid = "block-editor-live-preview" }) => {
  const kit = brand || {};
  const b = {
    accent_color: kit.accent_color || "#6366F1",
    text_color: kit.text_color || "#18181B",
    background_color: kit.background_color || "#FFFFFF",
    heading_font: kit.heading_font || "Plus Jakarta Sans",
    body_font: kit.body_font || "Inter",
    cover_style: kit.cover_style || "editorial",
    logo_url: kit.logo_url || "",
    header_text: kit.header_text || "",
    footer_text: kit.footer_text || "",
  };
  return (
    <div
      data-testid={testid}
      className="nv-doc mx-auto w-full max-w-[820px] rounded-xl shadow-2xl overflow-hidden"
      style={{
        background: b.background_color,
        color: b.text_color,
        "--doc-heading": b.heading_font,
        "--doc-body": b.body_font,
      }}
    >
      {b.header_text && (
        <div className="px-10 py-2 text-[10px] uppercase tracking-[0.2em] border-b" style={{ borderColor: "rgba(0,0,0,0.08)", opacity: 0.55 }}>
          {b.header_text}
        </div>
      )}
      <div className="px-6 sm:px-10 py-8">
        {(proposal.blocks || []).map((block) => (
          <BlockView
            key={block.id}
            block={block}
            brand={b}
            currency={proposal.currency || "INR"}
            gstMode={proposal.gst_mode || "none"}
            docTitle={proposal.title}
            clientName={proposal.client?.name}
            orgName={orgName}
            onTierSelect={onTierSelect}
            selectedTier={selectedTier}
            decision={proposal.decision}
          />
        ))}
      </div>
      {b.footer_text && (
        <div className="px-10 py-3 text-[10px] border-t flex justify-between" style={{ borderColor: "rgba(0,0,0,0.08)", opacity: 0.55 }}>
          <span>{b.footer_text}</span>
          <span className="font-num">1</span>
        </div>
      )}
    </div>
  );
};

export default DocPreview;
